import {NavLinkProps} from 'quickstart/components/layout/NavLink'
import {useUserData} from 'quickstart/hooks/useUserData'
import {Nullish, truthy} from 'quickstart/types'
import {meta} from 'tizra'

type FilterFn = (item: NavLinkProps) => any

const deepFilter = (fn: FilterFn) => {
  const df = (items: NavLinkProps[]): NavLinkProps[] =>
    items
      .filter(fn)
      .map(item => ('items' in item ? {...item, items: df(item.items)} : item))
  return df
}

type MapFn = (item: NavLinkProps) => NavLinkProps | Nullish

const deepMap = (fn: MapFn) => {
  const dm = (items: NavLinkProps[]): NavLinkProps[] =>
    items
      .map(fn)
      .filter(truthy)
      .map(item => ('items' in item ? {...item, items: dm(item.items)} : item))
  return dm
}

export const useNavItems = ({items}: {items: NavLinkProps[]}) => ({
  // Filter user-specific stuff out of the general nav. We didn't support this
  // prior to the nested nav, and it's better UX to keep general and
  // personalized nav separate, so don't open the door unnecessarily now.
  items: deepFilter(
    item => item && item.type !== 'account' && item.type !== 'bookshelf',
  )(items),
})

export type UseProfileItemsProps = {
  profileItems: NavLinkProps[]
  bookshelfConfig: {title: string; url: string}
  includeSignIn: boolean
}

export const useProfileItems = ({
  profileItems,
  bookshelfConfig,
  includeSignIn,
}: UseProfileItemsProps): {
  profileName: string
  profileItems: NavLinkProps[]
} => {
  const userData = useUserData({enabled: includeSignIn})
  const profileName = meta.userProfileName(userData)

  if (!includeSignIn || userData === null) {
    return {profileName, profileItems: []}
  }

  if (!userData) {
    return {
      profileName,
      profileItems: [
        {
          type: 'link',
          label: 'Sign in',
          _signin: true,
          // @ts-expect-error
          onClick: () => window.tizra.login(),
        },
      ],
    }
  }

  const filterFn: FilterFn = item => item.type !== 'expando'

  const mapFn: MapFn = item => {
    if (item.type === 'account') {
      return !userData
        ? null
        : {
            ...item,
            type: 'link',
            label: item.label || 'My Account',
            href: meta.userInfoHref(userData),
          }
    }
    if (item.type === 'bookshelf') {
      return !userData
        ? null
        : {
            ...item,
            type: 'link',
            label: item.label || bookshelfConfig.title || 'My Bookshelf',
            href: bookshelfConfig.url,
          }
    }
    return item
  }

  profileItems = deepMap(mapFn)(deepFilter(filterFn)(profileItems))

  if (userData) {
    if (profileItems.length) {
      profileItems.push({type: 'separator'})
    }
    profileItems.push({
      type: 'link',
      label: 'Sign out',
      // @ts-expect-error
      onClick: () => window.tizra.logout(),
    })
  }

  return {profileName, profileItems}
}
